/**
 * Table column sorting for **server-rendered** tables with class "sortable".
 * Reads sort key from the `data-sortkey` attribute on <td> elements.
 * Skips header cells with class `sorttable_nosort`.
 * Clicking a header toggles between ascending and descending order.
 *
 * ⚠️ **Only tables that exist when it runs.** `initTableSort()` scans `document`
 * at the moment it is called, so a table rendered later — inside a modal, say —
 * silently gets no handlers. `BriefsModal` carried `class="sortable"` on that
 * basis and its headers never once sorted. The dashboard hit the same thing from
 * the other side: its tables were bound by the boot call while the page was
 * server-rendered, and moving the page into the browser unbound them, so it calls
 * this again after each render (`features/dashboard/view.ts`, `rows.ts`).
 *
 * Re-running is cheap — a table is bound at most once, see `bound` below — but
 * running it over a **Preact-rendered table that will be diffed again** is not safe.
 * It reorders `<tr>` nodes with `appendChild`, and keyed rows mean the next diff
 * compares against a vdom whose order no longer matches the DOM. That is fine for
 * the tables here, which are replaced outright on the next render and never
 * re-diffed in place; a table whose data changes under it must sort its **data**
 * in state instead — see `BriefsModal`.
 */

/**
 * Tables already carrying handlers.
 *
 * Binding twice does not overwrite, it stacks: N listeners per header, each holding its
 * own `ascending` flag. They stay in step — one click advances all of them together — so
 * the *result* is right and this is not a correctness fix; it is N full sorts and N DOM
 * reorders per click on a table that may hold a thousand rows, plus a set of stale
 * closures over every table the page has ever rendered.
 *
 * A `WeakSet` keys off the element, so a table Preact replaces is simply a new entry and
 * the old one is collected with it.
 */
const bound = new WeakSet<HTMLTableElement>();

export function initTableSort(): void {
  document.querySelectorAll<HTMLTableElement>("table.sortable").forEach((table) => {
    const headerRow = table.querySelector("tr");
    if (!headerRow) return;
    if (bound.has(table)) return;
    bound.add(table);

    const headers = headerRow.querySelectorAll<HTMLElement>("td, th");
    headers.forEach((header, colIndex) => {
      if (header.classList.contains("sorttable_nosort")) return;
      header.style.cursor = "pointer";

      let ascending = true;

      header.addEventListener("click", () => {
        // Reset arrow indicators on all headers
        headers.forEach((h) => {
          const arrow = h.querySelector(".sort-arrow");
          if (arrow) arrow.remove();
        });

        const rows = Array.from(table.querySelectorAll("tr")).slice(1); // skip header
        rows.sort((a, b) => {
          const cellA = a.children[colIndex] as HTMLElement | undefined;
          const cellB = b.children[colIndex] as HTMLElement | undefined;
          const keyA = cellA?.getAttribute("data-sortkey") ?? cellA?.textContent?.trim() ?? "";
          const keyB = cellB?.getAttribute("data-sortkey") ?? cellB?.textContent?.trim() ?? "";

          // Try numeric comparison
          const numA = parseFloat(keyA);
          const numB = parseFloat(keyB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return ascending ? numA - numB : numB - numA;
          }

          // String comparison
          const cmp = keyA.localeCompare(keyB, undefined, { sensitivity: "base" });
          return ascending ? cmp : -cmp;
        });

        const parent = rows[0]?.parentNode;
        if (parent) {
          rows.forEach((row) => parent.appendChild(row));
        }

        // Add arrow indicator
        const arrow = document.createElement("span");
        arrow.className = "sort-arrow";
        arrow.style.marginLeft = "5px";
        arrow.style.fontSize = "0.8em";
        arrow.textContent = ascending ? "▲" : "▼";
        header.appendChild(arrow);

        ascending = !ascending;
      });
    });
  });
}
